import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AsyncState } from '@/hooks';
import { workflowsApi } from '@/api/workflows';
import type { WorkflowInstance } from '@/types/workflows';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import solutionMindset from '@/assets/images/undraw_solution-mindset_5xp7.svg';
import { formatRelative } from '@/utils/format';
import { groupInbox, stepProgress } from './inbox-grouping';
import { InstanceDetailModal } from './InstanceDetailModal';
import styles from '../AdminWorkflowsPage.module.css';

/**
 * The approvals inbox: every case whose CURRENT step names the signed-in
 * person. Grouped by process and ordered longest-waiting first (see
 * inbox-grouping.ts for why), each card carries exactly what a decision needs:
 * what the thing is, who asked, how far along it is, how long it has waited.
 *
 * Both decisions go through ConfirmActionDialog rather than a bare button: an
 * approval here IS the checker half of maker-checker, and the dialog's required
 * justification is what lands in the audit trail as the decision note. On
 * rejection that note is the whole point — a maker told "no" with no reason has
 * been told nothing.
 */

const NS = 'admin.workflows';

interface InboxTabProps {
  institutionId: string;
  state: AsyncState<WorkflowInstance[]>;
  onRetry: () => void;
  /** The inbox is stale the moment a decision lands; the parent reloads it. */
  onChanged: () => void;
  processTitles: Map<string, string>;
}

type PendingDecision = { instance: WorkflowInstance; verdict: 'APPROVE' | 'REJECT' } | null;

export function InboxTab({ institutionId, state, onRetry, onChanged, processTitles }: InboxTabProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const locale = i18n.language || 'en';

  const [pending, setPending] = useState<PendingDecision>(null);
  const [submitting, setSubmitting] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [detail, setDetail] = useState<WorkflowInstance | null>(null);

  const processTitle = (key: string) => processTitles.get(key) ?? key;

  const decide = async (note: string) => {
    if (!pending) return;
    setSubmitting(true);
    setDecisionError(null);
    try {
      await workflowsApi.member.decide(institutionId, pending.instance.id, pending.verdict, note);
      toast.success(t(pending.verdict === 'APPROVE' ? `${NS}.inbox.approved` : `${NS}.inbox.rejected`));
      setPending(null);
      onChanged();
    } catch (cause) {
      // The server's sentence, verbatim: a 403 ("you are not this step's
      // approver") and a 409 ("someone decided first") need different fixes and
      // the server is the only party that knows which happened.
      setDecisionError(cause instanceof Error ? cause.message : t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AsyncPanel
        state={state}
        onRetry={onRetry}
        isEmpty={(data) => data.length === 0}
        empty={
          <EmptyState
            illustration={solutionMindset}
            title={t(`${NS}.inbox.empty`)}
            description={t(`${NS}.inbox.emptyBody`)}
          />
        }
      >
        {(data) => (
          <div className={styles.inboxGroups}>
            {groupInbox(data).map((group) => (
              <section key={group.processKey} className={styles.processGroup}>
                <h2 className={styles.processGroupTitle}>{processTitle(group.processKey)}</h2>
                <ul className={styles.inboxList}>
                  {group.items.map((instance) => {
                    const progress = stepProgress(instance);
                    return (
                      <li key={instance.id}>
                        <Card className={styles.inboxCard}>
                          <div className={styles.inboxCardMain}>
                            <p className={styles.inboxSubject}>{instance.subjectLabel}</p>
                            <p className={styles.inboxMeta}>
                              {t(`${NS}.inbox.initiatedBy`, { name: instance.initiatedByName })}
                              {' · '}
                              {progress.total > 0
                                ? t(`${NS}.inbox.stepOf`, { current: progress.current, total: progress.total })
                                : t(`${NS}.chain.untitledStep`)}
                              {' · '}
                              {t(`${NS}.inbox.waitingSince`, {
                                when: formatRelative(instance.createdAt, locale),
                              })}
                            </p>
                          </div>
                          <div className={styles.inboxCardActions}>
                            <Button size="sm" variant="ghost" onClick={() => setDetail(instance)}>
                              {t(`${NS}.inbox.view`)}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setDecisionError(null);
                                setPending({ instance, verdict: 'REJECT' });
                              }}
                            >
                              {t(`${NS}.inbox.reject`)}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setDecisionError(null);
                                setPending({ instance, verdict: 'APPROVE' });
                              }}
                            >
                              {t(`${NS}.inbox.approve`)}
                            </Button>
                          </div>
                        </Card>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </AsyncPanel>

      <ConfirmActionDialog
        open={pending !== null}
        title={t(pending?.verdict === 'REJECT' ? `${NS}.inbox.rejectTitle` : `${NS}.inbox.approveTitle`)}
        intro={t(pending?.verdict === 'REJECT' ? `${NS}.inbox.rejectIntro` : `${NS}.inbox.approveIntro`)}
        tone={pending?.verdict === 'REJECT' ? 'critical' : 'neutral'}
        impactStatus="ready"
        impact={
          pending && (
            <dl className={styles.decisionImpact}>
              <div>
                <dt>{t(`${NS}.detail.subject`)}</dt>
                <dd>{pending.instance.subjectLabel}</dd>
              </div>
              <div>
                <dt>{t(`${NS}.detail.process`)}</dt>
                <dd>{processTitle(pending.instance.processKey)}</dd>
              </div>
              <div>
                <dt>{t(`${NS}.detail.initiatedBy`)}</dt>
                <dd>{pending.instance.initiatedByName}</dd>
              </div>
            </dl>
          )
        }
        confirmLabel={t(pending?.verdict === 'REJECT' ? `${NS}.inbox.reject` : `${NS}.inbox.approve`)}
        submitting={submitting}
        error={decisionError}
        onCancel={() => setPending(null)}
        onConfirm={decide}
      />

      <InstanceDetailModal
        instance={detail}
        institutionId={institutionId}
        processTitle={detail ? processTitle(detail.processKey) : ''}
        onClose={() => setDetail(null)}
        onChanged={() => {
          setDetail(null);
          onChanged();
        }}
      />
    </>
  );
}
